/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Grid,
  RACButton,
  RACModalCard,
  RACTable,
  RACTableCell,
  RACTableRow,
  Typography,
} from "@rentacenter/racstrap";
import { ExcelPreviewStyles } from "../Styles/ExcelPreview";
import { ParsedSheet } from "./ExcelPreviewTypes";
import ExcelPreviewTabs from "./ExcelPreviewTabs";
import ExcelPreviewPagination from "./ExcelPreviewPagination";

interface ExcelPreviewDialogProps {
  isOpen: boolean;
  fileName: string;
  parsedSheets: ParsedSheet[];
  onBack: () => void;
  onSave: () => void;
}

export default function ExcelPreviewDialog({
  isOpen,
  fileName,
  parsedSheets,
  onBack,
  onSave,
}: ExcelPreviewDialogProps) {
  const classes = ExcelPreviewStyles();

  const [activeSheetIndex, setActiveSheetIndex] = useState<number>(0);  // Tracks which sheet tab is currently active
  const [currentPage, setCurrentPage] = useState<number>(1);  // Tracks the current page number for pagination
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);  // Tracks how many rows are displayed per page

  // Reset all pagination state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setActiveSheetIndex(0);
      setCurrentPage(1);
      setRowsPerPage(25);
    }
  }, [isOpen]);

  // Reset currentPage to 1 when activeSheetIndex or rowsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSheetIndex, rowsPerPage]);

  const activeSheet: ParsedSheet | undefined = parsedSheets[activeSheetIndex];
  const totalRows = activeSheet ? activeSheet.rows.length : 0;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = activeSheet ? activeSheet.rows.slice(startIndex, endIndex) : [];

  const handleTabChange = (index: number) => {
    setActiveSheetIndex(index);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const renderTableHead = () => {
    if (!activeSheet) return <></>;
    return (
      <React.Fragment>
        <RACTableCell
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "#f5f5f5",
            fontFamily: "OpenSans-bold",
          }}
        >
          #
        </RACTableCell>
        {activeSheet.headers.map((header, index) => (
          <RACTableCell
            key={index}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "#f5f5f5",
              fontFamily: "OpenSans-bold",
            }}
          >
            <span className={classes.cellTruncate}>
              {header || `Col ${index + 1}`}
            </span>
          </RACTableCell>
        ))}
      </React.Fragment>
    );
  };

  const renderTableContent = () => {
    if (!activeSheet || totalRows === 0) {
      return <></>;
    }
    return (
      <React.Fragment>
        {currentRows.map((row, rowIndex) => {
          const rowNumber = (currentPage - 1) * rowsPerPage + rowIndex + 1;
          return (
            <RACTableRow key={rowIndex} className={classes.rowHover}>
              <RACTableCell>{rowNumber}</RACTableCell>
              {activeSheet.headers.map((_, colIndex) => {
                const cellValue =
                  row[colIndex] !== undefined && row[colIndex] !== null
                    ? String(row[colIndex])
                    : "";
                return (
                  <RACTableCell key={colIndex}>
                    <span className={classes.cellTruncate}>
                      {cellValue}
                    </span>
                  </RACTableCell>
                );
              })}
            </RACTableRow>
          );
        })}
      </React.Fragment>
    );
  };

  // Inject "Read Only" badge into the modal title after render
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        const titleElements = document.querySelectorAll('.MuiDialogTitle-root .MuiTypography-root');
        titleElements.forEach((el) => {
          const htmlEl = el as HTMLElement;
          if (htmlEl.textContent && htmlEl.textContent.includes(`Preview: ${fileName}`) && !htmlEl.querySelector('.read-only-badge')) {
            const badge = document.createElement('span');
            badge.className = 'read-only-badge';
            badge.textContent = 'Read Only';
            badge.style.cssText = 'background-color:#e0e0e0;color:#616161;font-size:0.75rem;font-family:OpenSans-semibold;padding:2px 10px;border-radius:12px;display:inline-block;margin-left:12px;vertical-align:middle;';
            htmlEl.style.display = 'inline-flex';
            htmlEl.style.alignItems = 'center';
            htmlEl.appendChild(badge);
          }
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, fileName]);

  const dialogContent = () => {
    return (
      <Grid className={classes.dialogContent}>
        {/* Table Section */}
        <Grid className={classes.tableContainer}>
          {activeSheet && totalRows > 0 ? (
            <RACTable
              renderTableHead={renderTableHead}
              renderTableContent={renderTableContent}
            />
          ) : activeSheet && totalRows === 0 ? (
            <>
              <RACTable renderTableHead={renderTableHead} />
              <Typography className={classes.noDataMessage}>
                No data in this sheet
              </Typography>
            </>
          ) : null}
        </Grid>

        {/* Bottom Bar: Tabs + Pagination */}
        <Grid container className={classes.bottomBar} justifyContent="space-between" alignItems="center">
          <Grid item>
            <ExcelPreviewTabs
              sheetNames={parsedSheets.map((s) => s.sheetName)}
              activeIndex={activeSheetIndex}
              onTabChange={handleTabChange}
            />
          </Grid>
          <Grid item>
            <ExcelPreviewPagination
              totalRows={totalRows}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Grid>
        </Grid>

        {/* Footer Actions */}
        <Grid className={classes.footerActions}>
          <RACButton
            variant="outlined"
            color="primary"
            onClick={onBack}
          >
            Back
          </RACButton>
          <RACButton
            variant="contained"
            color="primary"
            onClick={onSave}
          >
            Save
          </RACButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <RACModalCard
      isOpen={isOpen}
      closeIcon={true}
      onClose={onBack}
      maxWidth="lg"
      borderRadius="20px"
      title={`Preview: ${fileName}`}
      // eslint-disable-next-line react/no-children-prop
      children={dialogContent()}
    />
  );
}
